import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { DropdownButton, DropdownButtonProps } from './DropdownButton';
import { generateId } from '../../utils/DomUtil';
import { ButtonAttrs } from '../../utils/HtmlAttrs';
import { assignRef } from '../../utils/ReactHelpers';
import { FlyoutListMenuWidget, FlyoutListMenuProps } from '../FlyoutListMenuTextWidget';

export interface HierarchicalDropdownProps extends
  Omit<DropdownButtonProps, 'aria-controls' | 'focusOnClose' | 'onClose' | 'onOpen' | 'popupContent' | 'renderPopupContent'>,
  Pick<FlyoutListMenuProps, 'maxHeight' | 'menuItemInfos' | 'onSelect'> {

  /** Ref to the rendered button element */
  buttonRef?: React.Ref<HTMLButtonElement>;

  /** DOM id for the popup menu */
  menuId?: string;
}

/**
 * Convenience widget which combines a `DropdownButton` widget with a `FlyoutListMenuWidget`, with proper a11y.
 */
// tslint:disable-next-line:max-line-length
export const HierarchicalDropdownWidget = React.forwardRef<DropdownButton, HierarchicalDropdownProps & Omit<ButtonAttrs, keyof HierarchicalDropdownProps>>((props, ref) => {
  const { buttonRef, menuId, menuItemInfos, maxHeight, onSelect, ...dropdownProps } = props;
  const [isOpen, setOpen] = React.useState(false);
  const DefaultMenuId = React.useState(generateId())[0];
  const closeFn = React.useRef<(() => void) | null>(null);
  const buttonElement = React.useRef<HTMLButtonElement | null>(null);

  const menuProps: FlyoutListMenuProps = {
    allowAutoPosition: true,
    anchorEl: buttonElement.current,
    id: menuId || DefaultMenuId,
    maxHeight,
    menuItemInfos,
    menuStyle: { ...(isOpen && { minWidth: buttonElement.current!.getBoundingClientRect().width }) },
    onSelect: (value: string) => { onSelect(value); closeFn.current!(); },
    requestCloseAll: closeFn.current!,
    // tslint:disable-next-line:deprecation
    stackingRootId: props.stackingRootId,
    testId: props.testId!,
    toggleEl: buttonElement.current,
    zIndex: props.zIndex,
  };

  return <>
    <DropdownButton
      aria-controls={menuProps.id!}
      aria-haspopup='menu'
      {...dropdownProps}
      children={props.children || ''}
      focusOnClose
      onClose={() => { setOpen(false); closeFn.current = null; }}
      onOpen={doneFn => { closeFn.current = doneFn; setOpen(true); }}
      ref={dropdownRef => {
        assignRef(ref, dropdownRef);

        const dropdownElem = ReactDOM.findDOMNode(dropdownRef) as HTMLElement | null;
        buttonElement.current = dropdownElem ? dropdownElem.querySelector('[aria-haspopup]') : null;
        assignRef(buttonRef, buttonElement.current);
      }}
    />
    {!props.disabled && isOpen && <FlyoutListMenuWidget {...menuProps} />}
  </>;
});
HierarchicalDropdownWidget.displayName = 'HierarchicalDropdownWidget';
